/**
 * Fan automation config helpers for the fan device automations
 * (`fan_temperature_controller`, `fan_presence_timer` and
 * `fan_external_control`). Mirrors the backend models in
 * `otto/src/services/automations/types.ts`.
 */

import type { DeviceAutomationConfig } from '../stores/deviceAutomations';
import { field, wireObject } from './wire';

// ---------------------------------------------------------------------------
// Types (mirror backend — no shared package yet)
// ---------------------------------------------------------------------------

export const FAN_TEMP_CONTROLLER_TYPE = 'fan_temperature_controller' as const;
export const FAN_PRESENCE_TIMER_TYPE = 'fan_presence_timer' as const;
export const FAN_EXTERNAL_CONTROL_TYPE = 'fan_external_control' as const;

export const FAN_AUTOMATION_TYPES = [
  FAN_TEMP_CONTROLLER_TYPE,
  FAN_PRESENCE_TIMER_TYPE,
  FAN_EXTERNAL_CONTROL_TYPE,
] as const;

export type FanAutomationType = (typeof FAN_AUTOMATION_TYPES)[number];

export interface FanPidConfig {
  enabled: boolean;
  setpoint: number;
  kp: number;
  ki: number;
  kd: number;
  sample_interval_seconds: number;
  min_output: number;
  max_output: number;
}

export interface FanTemperatureControllerConfig {
  sensor_type: 'temperature' | 'humidity' | 'temperature_humidity';
  control_mode: 'threshold' | 'pid';
  low_threshold: number;
  high_threshold: number;
  idle_speed: number;
  low_speed: number;
  max_speed: number;
  hysteresis: number;
  pid: FanPidConfig;
  delay_on_seconds: number;
  delay_off_seconds: number;
}

export interface FanPresenceTimerConfig {
  startup_delay_seconds: number;
  operating_delay_seconds: number;
  idle_speed: number;
  operating_speed: number;
}

export type FanExternalSwitchMode = 'toggle' | 'speed_cycle' | 'follow' | 'momentary' | 'speed_up_down';

export interface FanExternalControlConfig {
  switch_mode: FanExternalSwitchMode;
  speed_steps: number;
  on_speed: number;
  min_speed: number;
  max_speed: number;
  speed_step_percent: number;
  debounce_ms: number;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export function getFanTempControllerDefaults(): FanTemperatureControllerConfig {
  return {
    sensor_type: 'temperature',
    control_mode: 'threshold',
    low_threshold: 24,
    high_threshold: 28,
    idle_speed: 0,
    low_speed: 50,
    max_speed: 100,
    hysteresis: 1,
    pid: {
      enabled: false,
      setpoint: 26,
      kp: 10,
      ki: 0.5,
      kd: 0,
      sample_interval_seconds: 30,
      min_output: 0,
      max_output: 100,
    },
    delay_on_seconds: 30,
    delay_off_seconds: 60,
  };
}

export function getFanPresenceTimerDefaults(): FanPresenceTimerConfig {
  return {
    startup_delay_seconds: 5,
    operating_delay_seconds: 300,
    idle_speed: 0,
    operating_speed: 100,
  };
}

export function getFanExternalControlDefaults(): FanExternalControlConfig {
  return {
    switch_mode: 'toggle',
    speed_steps: 3,
    on_speed: 100,
    min_speed: 25,
    max_speed: 100,
    speed_step_percent: 25,
    debounce_ms: 50,
  };
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function num(value: unknown, fallback: number, min?: number, max?: number): number {
  const n =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : fallback;
  const resolved = Number.isFinite(n) ? n : fallback;
  return min !== undefined && max !== undefined ? clamp(resolved, min, max) : resolved;
}

export function normalizeFanTempControllerConfig(raw?: unknown): FanTemperatureControllerConfig {
  const config = wireObject(raw) ?? {};
  const pid = field(config, 'pid');
  return {
    sensor_type:
      config.sensor_type === 'humidity' || config.sensor_type === 'temperature_humidity'
        ? config.sensor_type
        : 'temperature',
    control_mode: config.control_mode === 'pid' ? 'pid' : 'threshold',
    low_threshold: num(config.low_threshold, 24),
    high_threshold: num(config.high_threshold, 28),
    idle_speed: num(config.idle_speed, 0, 0, 100),
    low_speed: num(config.low_speed, 50, 0, 100),
    max_speed: num(config.max_speed, 100, 0, 100),
    hysteresis: Math.max(0, num(config.hysteresis, 1)),
    pid: {
      enabled: Boolean(field(pid, 'enabled')),
      setpoint: num(field(pid, 'setpoint'), 26),
      kp: num(field(pid, 'kp'), 10),
      ki: num(field(pid, 'ki'), 0.5),
      kd: num(field(pid, 'kd'), 0),
      sample_interval_seconds: Math.max(1, num(field(pid, 'sample_interval_seconds'), 30)),
      min_output: num(field(pid, 'min_output'), 0, 0, 100),
      max_output: num(field(pid, 'max_output'), 100, 0, 100),
    },
    delay_on_seconds: Math.max(0, num(config.delay_on_seconds, 30)),
    delay_off_seconds: Math.max(0, num(config.delay_off_seconds, 60)),
  };
}

export function normalizeFanPresenceTimerConfig(raw?: unknown): FanPresenceTimerConfig {
  const config = wireObject(raw) ?? {};
  return {
    startup_delay_seconds: Math.max(0, num(config.startup_delay_seconds, 5)),
    operating_delay_seconds: Math.max(0, num(config.operating_delay_seconds, 300)),
    idle_speed: num(config.idle_speed, 0, 0, 100),
    operating_speed: num(config.operating_speed, 100, 0, 100),
  };
}

export function normalizeFanExternalControlConfig(raw?: unknown): FanExternalControlConfig {
  const config = wireObject(raw) ?? {};
  const mode = config.switch_mode as FanExternalSwitchMode;
  const switchMode: FanExternalSwitchMode =
    mode === 'speed_cycle' || mode === 'follow' || mode === 'momentary' || mode === 'speed_up_down' ? mode : 'toggle';
  return {
    switch_mode: switchMode,
    speed_steps: Math.max(1, Math.min(10, Math.round(num(config.speed_steps, 3)))),
    on_speed: num(config.on_speed, 100, 0, 100),
    min_speed: num(config.min_speed, 25, 0, 100),
    max_speed: num(config.max_speed, 100, 0, 100),
    speed_step_percent: num(config.speed_step_percent, 25, 1, 100),
    debounce_ms: Math.max(0, num(config.debounce_ms, 50)),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function getFanTempControllerErrors(config: unknown): string[] {
  const normalized = normalizeFanTempControllerConfig(config);
  const errors: string[] = [];
  if (normalized.low_threshold >= normalized.high_threshold) {
    errors.push('Low threshold must be lower than the high threshold.');
  }
  if (normalized.control_mode === 'pid' && !normalized.pid.enabled) {
    errors.push('PID regulation is selected but the PID loop is disabled.');
  }
  return errors;
}

export function getFanPresenceTimerErrors(config: unknown): string[] {
  void normalizeFanPresenceTimerConfig(config);
  return [];
}

export function getFanExternalControlErrors(config: unknown): string[] {
  const normalized = normalizeFanExternalControlConfig(config);
  const errors: string[] = [];
  if (normalized.min_speed > normalized.max_speed) {
    errors.push('Min speed must be lower than or equal to the max speed.');
  }
  if (normalized.switch_mode === 'speed_up_down' && normalized.speed_step_percent <= 0) {
    errors.push('Speed step must be greater than zero.');
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Display models
// ---------------------------------------------------------------------------

export interface FanTempControllerDisplay {
  automationId: string;
  name: string;
  icon: string;
  enabled: boolean;
  controllerActive: boolean;
  controlSource: 'threshold' | 'pid' | 'none' | 'inhibited' | null;
  targetSpeed: number | null;
  appliedSpeed: number | null;
  sensorValue: number | null;
  config: FanTemperatureControllerConfig;
}

export interface FanPresenceTimerDisplay {
  automationId: string;
  name: string;
  icon: string;
  enabled: boolean;
  presenceActive: boolean;
  fanActive: boolean;
  timerActive: boolean;
  timeRemainingSeconds: number;
  targetSpeed: number | null;
  config: FanPresenceTimerConfig;
}

export interface FanExternalControlDisplay {
  automationId: string;
  name: string;
  icon: string;
  enabled: boolean;
  fanOn: boolean;
  lastSpeed: number | null;
  lastAction: string | null;
  config: FanExternalControlConfig;
}

function isTruthy(value: unknown): boolean {
  return value === true || value === 1 || value === 'true';
}

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function buildFanTempControllerDisplay(
  automation: {
    id: string;
    name: string;
    icon?: string;
    enabled: boolean;
    config: DeviceAutomationConfig;
  },
  currentValues: Record<string, unknown>,
): FanTempControllerDisplay {
  const sourceRaw = strOrNull(currentValues[`${FAN_TEMP_CONTROLLER_TYPE}__control_source`]);
  let controlSource: FanTempControllerDisplay['controlSource'] = null;
  if (sourceRaw === 'threshold' || sourceRaw === 'pid' || sourceRaw === 'none' || sourceRaw === 'inhibited') {
    controlSource = sourceRaw;
  }

  return {
    automationId: automation.id,
    name: automation.name,
    icon: automation.icon || 'thermostat',
    enabled: automation.enabled,
    controllerActive: isTruthy(currentValues[`${FAN_TEMP_CONTROLLER_TYPE}__controller_active`]),
    controlSource,
    targetSpeed: numOrNull(currentValues[`${FAN_TEMP_CONTROLLER_TYPE}__target_speed`]),
    appliedSpeed: numOrNull(currentValues[`${FAN_TEMP_CONTROLLER_TYPE}__applied_speed`]),
    sensorValue: numOrNull(currentValues[`${FAN_TEMP_CONTROLLER_TYPE}__sensor_value`]),
    config: normalizeFanTempControllerConfig(automation.config),
  };
}

export function buildFanPresenceTimerDisplay(
  automation: {
    id: string;
    name: string;
    icon?: string;
    enabled: boolean;
    config: DeviceAutomationConfig;
  },
  currentValues: Record<string, unknown>,
): FanPresenceTimerDisplay {
  return {
    automationId: automation.id,
    name: automation.name,
    icon: automation.icon || 'mode_fan',
    enabled: automation.enabled,
    presenceActive: isTruthy(currentValues[`${FAN_PRESENCE_TIMER_TYPE}__presence_active`]),
    fanActive: isTruthy(currentValues[`${FAN_PRESENCE_TIMER_TYPE}__fan_active`]),
    timerActive: isTruthy(currentValues[`${FAN_PRESENCE_TIMER_TYPE}__timer_active`]),
    timeRemainingSeconds: numOrNull(currentValues[`${FAN_PRESENCE_TIMER_TYPE}__time_remaining_seconds`]) ?? 0,
    targetSpeed: numOrNull(currentValues[`${FAN_PRESENCE_TIMER_TYPE}__target_speed`]),
    config: normalizeFanPresenceTimerConfig(automation.config),
  };
}

export function buildFanExternalControlDisplay(
  automation: {
    id: string;
    name: string;
    icon?: string;
    enabled: boolean;
    config: DeviceAutomationConfig;
  },
  currentValues: Record<string, unknown>,
): FanExternalControlDisplay {
  return {
    automationId: automation.id,
    name: automation.name,
    icon: automation.icon || 'switch',
    enabled: automation.enabled,
    fanOn: isTruthy(currentValues[`${FAN_EXTERNAL_CONTROL_TYPE}__fan_on`]),
    lastSpeed: numOrNull(currentValues[`${FAN_EXTERNAL_CONTROL_TYPE}__last_speed`]),
    lastAction: strOrNull(currentValues[`${FAN_EXTERNAL_CONTROL_TYPE}__last_action`]),
    config: normalizeFanExternalControlConfig(automation.config),
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function isFanAutomationType(automationType: string): boolean {
  return (FAN_AUTOMATION_TYPES as readonly string[]).includes(automationType);
}

export function formatDurationLabel(totalSeconds: number | null | undefined): string {
  if (!totalSeconds || totalSeconds <= 0) return '0m';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
}
